import { useDownloadsNotification } from '@common/hooks';
import {
  useCancelCarePlanGeneratingMutation,
  useCarePlanDownloadMutation,
  useCarePlanDownloadQuery,
  useDeleteCarePlanDownloadTableMutation,
  useRetryCarePlanGeneratingMutation,
} from '@common/cache';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import {
  IDownloadsTableEntry,
  IDownloadsTableFilters,
} from '@views/pages/Downloads/hooks/useDownloads.ts';
import { toNumber } from 'lodash';
import { useEffect, useState } from 'react';
import { DownloadsReportEventsType } from '@common/types';
import { debounce } from '@util/debounce.ts';

export type ColumnNames =
  'id' | 'fileName' | 'status' | 'generatedOn' | 'action';

const initialFilters: IDownloadsTableFilters = {
  page: 1,
  limit: 100,
};

const handledEventList: DownloadsReportEventsType[] = [
  'care-plan-download.progress',
  'care-plan-download.updated',
  'care-plan-download.completed',
];

export const useCarePlanDownloads = ({
  handleCloseDeleteModal,
  setIsDeleteButtonDisabled,
  setSelectedItems,
  selectedItems,
}: {
  handleCloseDeleteModal: () => void;
  setIsDeleteButtonDisabled: (isDisabled: boolean) => void;
  setSelectedItems: (selectedItems: IDownloadsTableEntry[]) => void;
  selectedItems: IDownloadsTableEntry[];
}) => {
  const { event } = useDownloadsNotification();
  const [entries, setEntries] = useState<IDownloadsTableEntry[]>([]);
  const [filters, setFilters] =
    useState<IDownloadsTableFilters>(initialFilters);
  const [reportsCurrentDownloadItem, setCurrentDownloadItem] =
    useState<IDownloadsTableEntry | null>(null);

  const { data, isPending, refetch } = useCarePlanDownloadQuery(filters);

  const { mutateAsync: downloadAsync, isPending: isDownloading } =
    useCarePlanDownloadMutation();

  const { mutateAsync: deleteAsync, isPending: isDeleting } =
    useDeleteCarePlanDownloadTableMutation();

  const { mutateAsync: cancelAsync, isPending: isCanceling } =
    useCancelCarePlanGeneratingMutation();

  const { mutateAsync: retryAsync, isPending: isRetrying } =
    useRetryCarePlanGeneratingMutation();

  const handleDownload = async (row: IDownloadsTableEntry) => {
    setCurrentDownloadItem(row);

    try {
      await downloadAsync({
        id: row.id,
        name: row.name,
        timestamp: row.generated_at,
      });
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setCurrentDownloadItem(null);
    }
  };

  const handleCancel = async (row: IDownloadsTableEntry) => {
    setCurrentDownloadItem(row);

    try {
      await cancelAsync(row.id);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setCurrentDownloadItem(null);
    }
  };

  const handleRetry = async (row: IDownloadsTableEntry) => {
    setCurrentDownloadItem(row);

    try {
      await retryAsync(row.id);
      toast.success('Retrying');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setCurrentDownloadItem(null);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteAsync(
        entries.filter(item => item.checked).map(entry => entry.id),
      );

      setFilters(initialFilters);
      handleCloseDeleteModal();
      toast.success('Deleted');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const setRowsSelection = (selectedIds: Set<string>) => {
    const next = entries.map(item => ({
      ...item,
      checked: selectedIds.has(item.id),
    }));
    setEntries(next);
    setSelectedItems(next);
  };

  const onPageIndexChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => {
    setFilters({ ...filters, page: page + 1 });
  };

  const onPageSizeChange = (event: {
    target: {
      value: string;
    };
  }) => {
    setFilters({ ...filters, limit: toNumber(event.target.value), page: 1 });
  };

  const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    debounce(
      () => setFilters({ ...filters, search: newValue, page: 1 }),
      true,
      500,
    );
  };

  useEffect(() => {
    if (data?.care_plan_downloads) {
      setEntries(
        data.care_plan_downloads.map(item => ({
          ...item,
          checked: !!selectedItems.find(entry => entry.id === item.id)?.checked,
          id: item.id,
          name: item.download_name,
          generated_at: item.generated_at,
        })),
      );
    }
  }, [data]);

  useEffect(() => {
    if (event && handledEventList.includes(event.type)) {
      setEntries(prevState =>
        prevState.map(item => ({
          ...item,
          ...(event.payload.care_plan_download_id === item.id &&
            event.type === 'care-plan-download.progress' && {
              download_percentage: event.payload?.percentage || 0,
            }),
          ...(event.payload.care_plan_download_id === item.id &&
            (event.type === 'care-plan-download.updated' ||
              event.type === 'care-plan-download.completed') && {
              status: event.payload?.status || item.status,
            }),
          ...(event.payload.care_plan_download_id === item.id &&
            event.type === 'care-plan-download.completed' && {
              generated_at: event.payload?.generated_at || item.generated_at,
              file_size: event.payload?.file_size || null,
            }),
        })),
      );
      if (
        event.type === 'care-plan-download.updated' &&
        event.payload?.status === 'failed' &&
        event.payload?.failure_reason
      ) {
        toast.error(event.payload?.failure_reason);
      }
      if (event.type === 'care-plan-download.completed') {
        refetch();
      }
    }
  }, [event]);

  useEffect(() => {
    setIsDeleteButtonDisabled(isDownloading || isCanceling || isDeleting);
  }, [isDownloading, isCanceling, isDeleting, entries]);

  return {
    filters,
    onPageIndexChange,
    onPageSizeChange,
    onSearchChange,
    entries,
    setRowsSelection,
    isLoading: isPending,
    handleDownload,
    reportsCurrentDownloadItem,
    disableDeleteButton:
      isDownloading ||
      isCanceling ||
      isRetrying ||
      entries.every(entry => !entry.checked) ||
      isDeleting,
    handleDelete,
    handleCancel,
    handleRetry,
    totalCount: data?.count ?? 0,
    isDeleting,
  };
};
